import { useEffect, useRef, useState } from 'react'

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3
}

export interface SyncedCountUpSpec {
  points: { from: number; to: number }
  credits: { from: number; to: number }
  nights: { from: number; to: number }
}

/**
 * Animates three numbers in one rAF loop with a single setState per frame (vs three `useCountUp` hooks),
 * which avoids triple re-renders competing with SVG progress transitions on cold-loaded pages.
 */
export default function useSyncedCountUp(
  spec: SyncedCountUpSpec | null,
  enabled: boolean,
  duration: number,
): { points: number; credits: number; nights: number } {
  const [value, setValue] = useState(() =>
    spec
      ? {
          points: spec.points.from,
          credits: spec.credits.from,
          nights: spec.nights.from,
        }
      : { points: 0, credits: 0, nights: 0 },
  )
  const rafRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled || !spec) {
      if (spec) {
        setValue({
          points: spec.points.from,
          credits: spec.credits.from,
          nights: spec.nights.from,
        })
      }
      return
    }

    const { points, credits, nights } = spec
    if (
      points.from === points.to
      && credits.from === credits.to
      && nights.from === nights.to
    ) {
      setValue({ points: points.to, credits: credits.to, nights: nights.to })
      return
    }

    startTimeRef.current = null

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp
      }

      const elapsed = timestamp - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeOutCubic(progress)

      setValue({
        points: Math.round(points.from + (points.to - points.from) * easedProgress),
        credits: Math.round(credits.from + (credits.to - credits.from) * easedProgress),
        nights: Math.round(nights.from + (nights.to - nights.from) * easedProgress),
      })

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [enabled, spec, duration])

  return value
}
